import { MessageCircle } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { formatDateTime } from "@buildpy/core/lib/format";
import { MAX_LOG_COMMENT_BODY, type LogCommentItem } from "@buildpy/core/modules/project/log-comment";
import { addProjectLogComment } from "@/api/actions/project-log";
import { useRecordAction } from "@/modules/project/ui/use-record-action";
import { photoViewKeys } from "@/modules/project/ui/use-photo-upload";
import { dropDetachedViews } from "@/lib/query";

interface Props {
  projectId: string;
  logId: string;
  comments: readonly LogCommentItem[];
}

// 쓴 사람 — 고객 글은 「고객」, 나간 직원은 「직원」.
export const commentAuthorOf = (c: Pick<LogCommentItem, "author_kind" | "author_name">): string =>
  c.author_kind === "CUSTOMER" ? "고객" : (c.author_name ?? "직원");

// 일지 아래 댓글 — 접힌 채 수만 보이고, 펼치면 목록과 한 줄 입력(고객 댓글에 답하는 자리).
const LogComments = ({ projectId, logId, comments }: Props) => {
  const { pending, run } = useRecordAction();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const length = Array.from(body).length;
  const blocked = pending || body.trim() === "" || length > MAX_LOG_COMMENT_BODY;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    void run("댓글을 남기지 못했습니다", () => addProjectLogComment(projectId, { log_id: logId, body }), () => {
      dropDetachedViews(photoViewKeys(projectId));
      setBody("");
    });
  };

  return (
    <div className="mt-1.5 flex flex-col gap-y-1.5" data-testid="log-comments">
      <Button
        aria-expanded={open}
        className="self-start"
        data-testid="log-comments-toggle"
        size="sm"
        type="button"
        variant="ghost-muted"
        onClick={() => setOpen((v) => !v)}>
        <MessageCircle />
        {comments.length > 0 ? `댓글 ${comments.length}` : "댓글 달기"}
      </Button>
      {open && (
        <div className="flex flex-col gap-y-2 border-t border-border pt-2">
          {comments.length > 0 && (
            <ol className="flex flex-col gap-y-1.5" data-testid="log-comment-list">
              {comments.map((c) => (
                <li key={c.id} className="flex flex-col gap-y-0.5">
                  <p className="flex items-center justify-between gap-2 text-xs">
                    <span className="font-medium">{commentAuthorOf(c)}</span>
                    <span className="tabular-nums text-muted-foreground">{formatDateTime(c.created_at)}</span>
                  </p>
                  <p className="whitespace-pre-wrap break-words">{c.body}</p>
                </li>
              ))}
            </ol>
          )}
          <form className="flex items-center gap-x-2" data-testid="log-comment-form" onSubmit={submit}>
            <Input
              aria-label="댓글"
              className="min-h-10 min-w-0 flex-1"
              name="log-comment"
              variant="outline"
              placeholder="댓글을 남겨 주세요"
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <Button disabled={blocked} size="sm" type="submit">
              남기기
            </Button>
          </form>
          {length > MAX_LOG_COMMENT_BODY && (
            <p className="text-xs text-destructive">
              {length}/{MAX_LOG_COMMENT_BODY}자 — 줄여 주세요
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default LogComments;
